import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useMemo } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { useForm, useFormContext, useWatch } from 'react-hook-form';
import useSWR, { mutate } from 'swr';
import { z } from 'zod';

import {
  associateFileToEntity,
  getFilesAssociatedToEntity,
  updateAssociationFileToEntity,
} from '~/components/utils/upload';
import { certificatesCreate } from '~/data/generated/endpoints/certificates/certificates';
import { propertiesFormList } from '~/data/generated/endpoints/properties/properties';
import type { PropertyForm } from '~/data/generated/model';
import type {
  API_CertificateCreateRequest,
  API_CertificateCreateRequestResponse,
} from '~/data/schemas/certificate';
import { VALID_DATE_YEAR_MESSAGE, validateDateYear } from '~/data/schemas/zodHelpers';
import useLAISnackbar, { ActionEnum } from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { usePropertyCars } from '~/hooks/usePropertyCars';

import type { FieldDef } from '../form/utils';
import { createFormInvalidHandler, FieldAndCell } from '../form/utils';
import type {
  LAIUseFormReturn,
  LiftAIDocumentTagged,
  LiftAIFormProps,
} from './SelectDocumentTypeDialog';

const certificateFormSchema = z
  .object({
    // Nullish-tolerant for the same reason as `propertyId` below: the create
    // request type this form is seeded from declares `jurisdiction` nullable.
    jurisdiction: z
      .union([z.string(), z.null(), z.undefined()])
      .transform((val) => val ?? '')
      .pipe(z.string().min(1, { message: 'Jurisdiction is required' })),
    dateIssued: z
      .string()
      .min(1, { message: 'Issue date is required' })
      .refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }),
    expirationDate: z
      .string()
      .min(1, { message: 'Expiration date is required' })
      .refine(validateDateYear, { message: VALID_DATE_YEAR_MESSAGE }),
    // `z.preprocess` types its input as `unknown`, which made the form's field
    // type unusable once the resolver started inferring it. The union keeps the
    // same runtime coercion with an input type the fields can actually hold.
    propertyId: z
      .union([z.string(), z.null(), z.undefined()])
      .transform((val) => val ?? '')
      .pipe(z.string().min(1, { message: 'Property is required' })),
    carId: z
      .union([z.string(), z.null(), z.undefined()])
      .transform((val) => val ?? '')
      .pipe(z.string().min(1, { message: 'This field is required' })),
    // Drives whether the property and car pickers are editable. It is form
    // state rather than API data, but the resolver's field-values type has to
    // cover every key the form holds. `stripIsCarChangeable` keeps it out of
    // the create request.
    isCarChangeable: z.boolean().optional(),
  })
  .refine(
    (data) => {
      const issueDate = new Date(data.dateIssued);
      const expirationDate = new Date(data.expirationDate);
      return expirationDate > issueDate;
    },
    {
      message: 'Expiration date must be after the issue date',
      path: ['expirationDate'],
    },
  );

const certificateOptionsFetcher = async (): Promise<{ properties: PropertyForm[] }> => {
  const { data: properties } = await propertiesFormList();
  return { properties };
};

interface CertificateFieldsProps {
  extractedPropertyText?: string;
  extractedCarText?: string;
  disabled?: boolean;
}

export const CertificateFields = ({
  extractedPropertyText,
  extractedCarText,
  disabled,
}: CertificateFieldsProps = {}) => {
  const { control, getValues } = useFormContext<CertificateFormData>();
  const propertyId = useWatch({ control, name: 'propertyId' }) ?? '';
  const isCarChangeable = useWatch({ control, name: 'isCarChangeable' });
  const terms = useLocaleTerms();

  const { data } = useSWR('CertificateForm.Options', certificateOptionsFetcher);
  const { cars, loading: carsLoading } = usePropertyCars(propertyId);

  const formFields: FieldDef[] = useMemo(
    () =>
      [
        {
          name: 'propertyId',
          label: 'Property',
          type: 'autocomplete' as const,
          options:
            data?.properties?.map((property) => ({
              id: property.id,
              label: property.name,
            })) ?? [],
          loading: !data?.properties,
          required: true,
          disabled: !isCarChangeable,
          gridProps: { xs: 12 },
          autoCompleteProps: {
            value: getValues('propertyId') ?? '',
            isOptionEqualToValue: (option: { id: string; label: string }, value: string) => {
              return option.id === value;
            },
          },
        },
        {
          name: 'carId',
          label: terms.vehicle,
          type: 'autocomplete',
          options:
            cars?.map((car) => ({
              id: car.id,
              label: car.name + (car.ahjId ? ` - ${car.ahjId}` : ''),
            })) ?? [],
          loading: carsLoading,
          required: true,
          disabled: !isCarChangeable || !propertyId,
          gridProps: { xs: 12 },
          autoCompleteProps: {
            value: getValues('carId') ?? '',
            isOptionEqualToValue: (option: { id: string; label: string }, value: string) => {
              return option.id === value;
            },
          },
        },
        {
          name: 'jurisdiction',
          label: 'Jurisdiction',
          type: 'text',
          required: true,
          gridProps: { xs: 12 },
        },
        {
          name: 'dateIssued',
          label: 'Date Issued',
          type: 'date',
          required: true,
          gridProps: { xs: 6 },
        },
        {
          name: 'expirationDate',
          label: 'Expiration Date',
          type: 'date',
          required: true,
          gridProps: { xs: 6 },
        },
      ].map((f) => (disabled ? { ...f, disabled: true } : f)) as FieldDef[],
    [data?.properties, cars, carsLoading, getValues, isCarChangeable, propertyId, disabled, terms],
  );

  return (
    <>
      <Typography variant="h6" my={2}>
        General
      </Typography>
      {extractedPropertyText || extractedCarText ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Extracted from document:</strong>
          </Typography>
          {extractedPropertyText ? (
            <Typography variant="body2">Property: {extractedPropertyText}</Typography>
          ) : null}
          {extractedCarText ? (
            <Typography variant="body2">
              {terms.vehicle}: {extractedCarText}
            </Typography>
          ) : null}
        </Alert>
      ) : null}
      <Grid container spacing={2}>
        {formFields.map((field) => (
          <FieldAndCell key={field.name} field={field} control={control} />
        ))}
      </Grid>
    </>
  );
};

// What the fields hold. `propertyId` and `carId` accept the nullish values the
// AI-extraction path can seed before the transform normalizes them.
type CertificateFormData = z.input<typeof certificateFormSchema>;
type CertificateParsedData = z.output<typeof certificateFormSchema>;

export const getDefaultValues = (): DefaultValues<CertificateFormData> => ({
  isCarChangeable: true,
  jurisdiction: '',
  dateIssued: '',
  expirationDate: '',
  propertyId: '',
  carId: '',
});

const CertificateForm: React.FC<LiftAIFormProps<CertificateFormData>> = ({
  children,
  document,
  onSubmit,
  initialData,
}) => {
  const { showEntityActionSnackbar, showSnackbar } = useLAISnackbar();
  const { handleSubmit, reset } = useFormContext<CertificateFormData>();

  useEffect(() => {
    if (!initialData) {
      return;
    }
    // Merge over the full defaults. This used to re-seed only propertyId, which
    // left every other omitted key (carId when the URL carries no carId) blanked
    // by reset()'s wholesale replacement. Same class as AMP-691.
    reset({
      ...getDefaultValues(),
      ...initialData,
    });
  }, [initialData, reset]);

  const updateReferenceToFiles = async (
    certificate: API_CertificateCreateRequestResponse,
    document: LiftAIDocumentTagged,
  ) => {
    if (certificate.id) {
      const attachments = await getFilesAssociatedToEntity(certificate.id, 'certificate');

      await Promise.all(
        attachments
          .filter((attachment) => attachment.isPrimary)
          .map((attachment) =>
            updateAssociationFileToEntity(attachment.attachment, certificate.id, 'certificate', {
              id: attachment.id,
              isVoid: true,
            }),
          ),
      );
    }

    await associateFileToEntity(
      document.attachment.id,
      certificate.id,
      'certificate',
      document.fileType === 'pdf'
        ? {
            pageNumber: document.pageNum,
            isPrimary: true,
          }
        : {},
    );
  };

  const saveCertificate = async (
    dto: API_CertificateCreateRequest,
  ): Promise<API_CertificateCreateRequestResponse> => {
    const { data } = await certificatesCreate(dto);
    return data;
  };

  return (
    <form
      noValidate
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onSubmit={handleSubmit(
        async (data) => {
          try {
            const validatedData = certificateFormSchema.parse(data);
            const {
              propertyId: property_id,
              isCarChangeable: _isCarChangeable,
              ...apiRequest
            } = validatedData;

            const certificate = await saveCertificate({
              ...apiRequest,
              attachmentId: document?.attachment.id ?? null,
            });

            if (document) {
              await updateReferenceToFiles(certificate, document);
            }

            onSubmit(certificate);

            void mutate((key) => {
              if (!Array.isArray(key)) return false;
              const [keyType, keySubType, keyId] = key;

              if (keyType === 'certificate' && typeof keySubType === 'object') return true;
              if (keyType === 'certificate' && keySubType === 'car' && keyId === apiRequest.carId)
                return true;
              if (keyType === 'compliance') return true;
              if (keyType === 'compliance' && keySubType === 'car' && keyId === apiRequest.carId)
                return true;

              return false;
            });

            showEntityActionSnackbar(
              {
                name: 'Certificate',
                action: ActionEnum.Create,
                id: validatedData.carId,
              },
              {
                variant: 'success',
              },
            );
          } catch (err) {
            console.error('Certificate form error:', err);
            showSnackbar('Certificate failed to save', {
              variant: 'error',
            });
          }
        },
        createFormInvalidHandler<CertificateFormData>({
          formName: 'CertificateForm',
          showSnackbar,
        }),
      )}
    >
      {children}
    </form>
  );
};

function useCertificateForm(): LAIUseFormReturn<CertificateFormData, CertificateParsedData> {
  const hookForm = useForm<CertificateFormData, unknown, CertificateParsedData>({
    mode: 'all',
    resolver: zodResolver(certificateFormSchema),
    defaultValues: getDefaultValues(),
  });

  return {
    hookForm,
    Form: CertificateForm,
    Fields: CertificateFields as unknown as LAIUseFormReturn<
      CertificateFormData,
      CertificateParsedData
    >['Fields'],
  };
}

export { useCertificateForm };
